import {
  Cloud,
  CloudFog,
  CloudLightning,
  CloudRain,
  CloudSun,
  Snowflake,
  Sun,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useParkWeather } from '../hooks/useApi';

function weatherInfo(code: number): { Icon: LucideIcon; label: string } {
  if (code === 0) return { Icon: Sun, label: 'Clear' };
  if (code === 1 || code === 2) return { Icon: CloudSun, label: 'Partly cloudy' };
  if (code === 3) return { Icon: Cloud, label: 'Cloudy' };
  if (code === 45 || code === 48) return { Icon: CloudFog, label: 'Fog' };
  if ((code >= 51 && code <= 67) || (code >= 80 && code <= 82)) return { Icon: CloudRain, label: 'Rain' };
  if ((code >= 71 && code <= 77) || code === 85 || code === 86) return { Icon: Snowflake, label: 'Snow' };
  if (code >= 95) return { Icon: CloudLightning, label: 'Storms' };
  return { Icon: Cloud, label: 'Cloudy' };
}

export function WeatherCard({ lat, lon }: { lat: number; lon: number }) {
  const { data } = useParkWeather(lat, lon);
  if (!data) return null;

  const current = weatherInfo(data.current.weatherCode);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
      <p className="text-xs font-semibold tracking-widest uppercase text-emerald-700">
        What to expect
      </p>
      <div className="mt-3 flex items-center gap-3">
        <current.Icon className="w-8 h-8 text-gray-500" />
        <div>
          <p className="text-3xl font-bold text-gray-900 tabular-nums">
            {Math.round(data.current.temperature)}°
          </p>
          <p className="text-sm text-gray-600">{current.label}</p>
        </div>
      </div>
      {data.daily.length > 0 && (
        <div className="mt-4 grid grid-cols-5 gap-1">
          {data.daily.slice(0, 5).map((day) => {
            const info = weatherInfo(day.weatherCode);
            const weekday = new Date(`${day.date}T00:00:00`).toLocaleDateString('en-US', {
              weekday: 'narrow',
            });
            return (
              <div key={day.date} className="flex flex-col items-center gap-1">
                <p className="text-xs text-gray-500">{weekday}</p>
                <info.Icon className="w-4 h-4 text-gray-500" />
                <p className="text-xs tabular-nums text-gray-700">{Math.round(day.high)}°</p>
                <p className="text-xs tabular-nums text-gray-400">{Math.round(day.low)}°</p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
